import { AnimatePresence, motion } from "framer-motion";
import {useEffect, useState, useRef, forwardRef, useImperativeHandle} from "react";
import toast from "react-hot-toast";
import { uniqueId } from "lodash";
import { LiveAudioVisualizer } from "react-audio-visualize";
import { MediaRecorder as ExtendableMediaRecorder, register } from "extendable-media-recorder";
import { connect } from "extendable-media-recorder-wav-encoder";

let isEncoderConnected = false;

const AudioRecording = forwardRef(({
  clickAudioRecordingHandler,
  recordedDataHandler,
}, ref) => {
  const texts = ["1", "2", "3", "GO!"];
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [audioRecordingStarted, setAudioRecordingStarted] = useState(false);
  const [time, setTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const [canStop, setCanStop] = useState(false);
  const intervalRef = useRef(null);
  const shouldSaveRef = useRef(true);
  const streamRef = useRef(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  useEffect(() => {
    if (time >= 10) setCanStop(true);
    if (time >= 60) {
      shouldSaveRef.current = true;
      stopRecording();
    }
  }, [time]);

  const generateUniqueName = (sizeInMB) => {
    const randomDigits = Math.floor(Math.random() * 1e16)
      .toString()
      .padStart(16, "0");
    return `record-${randomDigits}.wav`;
  };

  const startRecording = async () => {
    try {
      if (!isEncoderConnected) {
        await register(await connect());
        isEncoderConnected = true;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new ExtendableMediaRecorder(stream, { mimeType: 'audio/wav' });
      const recorderVisualizer = new MediaRecorder(stream);
      setMediaRecorder(recorderVisualizer);
      mediaRecorderRef.current = recorder;

      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);

      recorder.onstop = async () => {
        if (!shouldSaveRef.current) return;

        const blob = new Blob(chunks, { type: 'audio/wav' });

        // Get precise duration using AudioContext
        const arrayBuffer = await blob.arrayBuffer();
        const audioContext = new AudioContext();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const durationInSeconds = audioBuffer.duration.toFixed(0);

        const sizeInMB = (blob.size / (1024 * 1024)).toFixed(2);
        const name = generateUniqueName(sizeInMB);

        recordedDataHandler({
          id: uniqueId(),
          blob: blob,
          name: name,
          time: durationInSeconds,
          size: sizeInMB
        });
      };

      recorder.start();
      recorderVisualizer.start();
      const intervalId = setInterval(() => {
        setTime((prev) => prev + 1);
      }, 1000);

      const updateTimer = () => {
        intervalRef.current = requestAnimationFrame(updateTimer);
      };
      intervalRef.current = requestAnimationFrame(updateTimer);
      return () => clearInterval(intervalId);
    } catch (error) {
      console.error("Failed to start recording:", error);
      toast.error("Recording failed.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorder.stop();

      const stream = streamRef.current;
      if (stream) {
        stream.getAudioTracks().forEach((track) => track.stop());
      }

      setCanStop(false);
      cancelAnimationFrame(intervalRef.current);
      clickAudioRecordingHandler();
    }
    else{
      clickAudioRecordingHandler();
    }
  };

  useEffect(() => {
    if (index < texts.length) {
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          setIndex((prev) => prev + 1);
          setVisible(true);
        }, 250);
      }, 750);
      return () => clearTimeout(timer);
    } else {
      setTimeout(() => {
        setAudioRecordingStarted(true);
        startRecording();
      }, 1);
    }
  }, [index]);


  useImperativeHandle(ref, () => ({
    cancelRecording: () => {
      shouldSaveRef.current = false;
      stopRecording();
      setAudioRecordingStarted(false);
    }
  }));

  return (
    <div>
      {!audioRecordingStarted && (
        <div>
          <div className="flex items-center justify-center  text-slate-700 text-2xl font-bold">
            <AnimatePresence mode="wait">
              {visible && index < texts.length && (
                <motion.div
                  key={texts[index]}
                  initial={{ scale: 0.5, opacity: 0, x: 0 }}
                  animate={{ scale: 1.5, opacity: 1, x: 0 }}
                  exit={{ scale: 1.2, opacity: 0, x: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {texts[index]}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <p className="text-[13px] mt-6 text-center text-slate-500">
            Recording will begin after a 3-second countdown. Please be ready.
          </p>
        </div>
      )}

      {audioRecordingStarted && (
        <div className="flex flex-col justify-center items-center">
          <p className="text-2xl text-slate-800 font-bold">{`00:${time
            .toString()
            .padStart(2, "0")}`}</p>
          <p className="text-[12px] mt-1 text-slate-400">
            Record up to 60 seconds
          </p>
          <div className="mt-2">
            {mediaRecorder && (
              <div className="flex flex-row justify-center items-center gap-[3px]">
                <div className="-rotate-180">
                  <LiveAudioVisualizer
                    mediaRecorder={mediaRecorder}
                    width={90}
                    height={10}
                    barWidth={1}
                    gap={3}
                    backgroundColor="#fcfcfd"
                    barColor="#9381e4"
                    fftSize={256}
                    smoothingTimeConstant={0.9}
                  />
                </div>
                <div>
                  <LiveAudioVisualizer
                    mediaRecorder={mediaRecorder}
                    width={90}
                    height={10}
                    barWidth={1}
                    gap={3}
                    backgroundColor="#fcfcfd"
                    barColor="#9381e4"
                    fftSize={256}
                    smoothingTimeConstant={0.9}
                  />
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              shouldSaveRef.current = true;
              canStop
                ? stopRecording()
                : toast.error(
                    "Each recording must be between 10 and 60 seconds in length."
                  );
            }}
            className="border-2 cursor-pointer mt-6 flex justify-center items-center border-slate-400 rounded-full h-[35px] w-[35px]"
          >
            <div className="h-[15px] w-[15px] rounded-[4px] bg-red-500 "></div>
          </button>
        </div>
      )}

      <i
        onClick={() => {
          shouldSaveRef.current = false;
          setAudioRecordingStarted(false);
          stopRecording();
        }}
        className="z-50 absolute top-2 right-4 text-gray-500 text-xl font-bold hgi hgi-stroke hgi-cancel-01"
      ></i>
    </div>
  );
});

export default AudioRecording;